import { mdiClose, mdiMicrophone, mdiPause, mdiPlay } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, IconButton, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface VoiceMessagePreviewProps {
	file: File;
	uploadProgress?: number;
	isComplete?: boolean;
	hasError?: boolean;
	onRemove: () => void;
	canRemove?: boolean;
}

export const VoiceMessagePreview: React.FC<VoiceMessagePreviewProps> = ({
	file,
	uploadProgress = 0,
	isComplete = false,
	hasError = false,
	onRemove,
	canRemove = true,
}) => {
	const { t } = useTranslation();
	const [audioUrl, setAudioUrl] = useState<string | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState<number>(0);
	const [currentTime, setCurrentTime] = useState<number>(0);
	const audioRef = useRef<HTMLAudioElement | null>(null);

	// Create audio URL and setup event listeners
	useEffect(() => {
		const objectUrl = URL.createObjectURL(file);
		setAudioUrl(objectUrl);

		const audio = new Audio(objectUrl);
		audioRef.current = audio;

		const handleLoadedMetadata = () => {
			if (!isNaN(audio.duration) && isFinite(audio.duration)) {
				setDuration(audio.duration);
			} else {
				// Fallback: try to get duration after a short delay
				setTimeout(() => {
					if (!isNaN(audio.duration) && isFinite(audio.duration)) {
						setDuration(audio.duration);
					} else {
						setDuration(0);
					}
				}, 500);
			}
		};

		const handleTimeUpdate = () => {
			if (!isNaN(audio.currentTime)) {
				setCurrentTime(audio.currentTime);
			}
		};

		const handleEnded = () => {
			setIsPlaying(false);
			setCurrentTime(0);
		};

		audio.addEventListener('loadedmetadata', handleLoadedMetadata);
		audio.addEventListener('timeupdate', handleTimeUpdate);
		audio.addEventListener('ended', handleEnded);

		// Cleanup
		return () => {
			audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
			audio.removeEventListener('timeupdate', handleTimeUpdate);
			audio.removeEventListener('ended', handleEnded);
			URL.revokeObjectURL(objectUrl);
			audio.pause();
		};
	}, [file]);

	const togglePlayPause = () => {
		if (audioRef.current) {
			if (isPlaying) {
				audioRef.current.pause();
				setIsPlaying(false);
			} else {
				audioRef.current.play();
				setIsPlaying(true);
			}
		}
	};

	const formatTime = (time: number) => {
		if (isNaN(time) || !isFinite(time) || time < 0) {
			return '0:00';
		}
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	};

	const getStatusText = () => {
		if (hasError) return 'Error';
		if (isComplete) return t('chats.readyToSend');
		if (uploadProgress > 0) return `${uploadProgress}%`;
		return 'Preparing...';
	};

	const getStatusColor = () => {
		if (hasError) return 'error.main';
		if (isComplete) return 'success.main';
		return 'text.secondary';
	};

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				padding: '8px 12px',
				border: '1px solid #e0e0e0',
				borderRadius: '12px',
				backgroundColor: hasError ? 'rgba(211, 47, 47, 0.1)' : '#f0f2f5',
				minWidth: '200px',
				maxWidth: '300px',
			}}
		>
			{/* Microphone icon */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: 32,
					height: 32,
					borderRadius: '50%',
					backgroundColor: 'primary.main',
					color: 'white',
					flexShrink: 0,
				}}
			>
				<Icon path={mdiMicrophone} size={0.7} />
			</Box>

			{/* Waveform/duration area */}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{/* Play/Pause button */}
					<IconButton
						size="small"
						onClick={togglePlayPause}
						disabled={!isComplete || hasError}
						sx={{
							width: 24,
							height: 24,
							color: 'primary.main',
							'&:disabled': { color: 'text.disabled' },
						}}
					>
						<Icon path={isPlaying ? mdiPause : mdiPlay} size={0.6} />
					</IconButton>

					{/* Duration/Progress */}
					<Typography variant="caption" color="text.secondary">
						{isPlaying ? formatTime(currentTime) : formatTime(duration)}
					</Typography>

					{/* Simple progress bar */}
					{duration > 0 && (
						<Box
							sx={{
								flex: 1,
								height: 2,
								backgroundColor: 'rgba(0,0,0,0.1)',
								borderRadius: 1,
								overflow: 'hidden',
							}}
						>
							<Box
								sx={{
									width: `${(currentTime / duration) * 100}%`,
									height: '100%',
									backgroundColor: 'primary.main',
									transition: 'width 0.1s ease',
								}}
							/>
						</Box>
					)}
				</Box>

				{/* Status and duration */}
				<Box
					sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}
				>
					<Typography
						variant="caption"
						sx={{
							color: getStatusColor(),
							fontSize: '0.7rem',
						}}
					>
						{getStatusText()}
					</Typography>

					{duration > 0 && (
						<Typography
							variant="caption"
							sx={{
								color: 'text.secondary',
								fontSize: '0.7rem',
							}}
						>
							{formatTime(duration)}
						</Typography>
					)}
				</Box>
			</Box>

			{/* Remove button */}
			{canRemove && (
				<IconButton
					size="small"
					onClick={onRemove}
					sx={{
						padding: '4px',
						color: 'text.secondary',
						'&:hover': { color: 'error.main' },
					}}
				>
					<Icon path={mdiClose} size={0.6} />
				</IconButton>
			)}
		</Box>
	);
};
